import { is } from "ramda";
import { type FC, type PropsWithChildren, type ReactNode, useEffect, useMemo, useRef } from "react";
import type { Key } from "react-aria";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { FixedPanel } from "./components/FixedPanel.tsx";
import { type PanelApi, PanelGroup, type PanelGroupProps } from "./PanelGroup.tsx";
import { PanelPosition } from "./settings.ts";
import { createPanelLayoutStore, PanelLayoutContext, usePanelLayoutStore } from "./stores/panel-store.ts";

export interface PanelLayoutProps extends PanelGroupProps {
  defaultTabId?: Key;
  tabsActions?: ReactNode;
  openOnTabPress?: boolean;
}

const Layout: FC<PropsWithChildren<PanelLayoutProps>> = ({ children, ...props }) => {
  const setRef = usePanelLayoutStore((state) => state.setPanelApiRef);
  const setActiveTabId = usePanelLayoutStore((state) => state.setActiveTabId);
  const panelApiRef = useRef<PanelApi>(null);

  const dataAttributes = useDataAttributes(props);

  useEffect(() => {
    if (props.defaultTabId) {
      setActiveTabId(props.defaultTabId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Need to overwrite the API because the usage of PanelOrientation in the API
    setRef(panelApiRef);
    props.apiRef = panelApiRef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orientation]);

  const willNeverBeHidden = useMemo(() => is(Number, props.closedSize) && props.closedSize > 0, [props.closedSize]);

  // Need a Fragment for the Children because Panel only supports 2 children at the moment.
  return (
    <PanelGroup apiRef={panelApiRef} {...props}>
      {props.fixedPanel === PanelPosition.Leading ? (
        <FixedPanel
          willNeverBeHidden={willNeverBeHidden}
          tabsActions={props.tabsActions}
          openOnTabPress={props.openOnTabPress}
          {...dataAttributes}
        />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{children}</>
      )}
      {props.fixedPanel === PanelPosition.Leading ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{children}</>
      ) : (
        <FixedPanel
          willNeverBeHidden={willNeverBeHidden}
          tabsActions={props.tabsActions}
          openOnTabPress={props.openOnTabPress}
          {...dataAttributes}
        />
      )}
    </PanelGroup>
  );
};

export const PanelLayout: FC<PropsWithChildren<PanelLayoutProps>> = ({ children, ...props }) => {
  const store = useRef(createPanelLayoutStore(props.defaultOpen)).current;

  return (
    <PanelLayoutContext.Provider value={store}>
      <Layout {...props}>{children}</Layout>
    </PanelLayoutContext.Provider>
  );
};
